<script setup lang="ts">
import type { SimpleNotification } from '@/composables/useNotifications'
import Checkbox from '@/components/ui/Checkbox.vue'
import { inject, type Ref, ref } from 'vue'
import { formatDate } from '@/lib/date'

defineProps<{
  notification: SimpleNotification
  selectable?: boolean
  isLinked?: boolean
}>()

const selectedNotificationIds = inject<Ref<Set<string>>>('selectedNotificationIds', ref(new Set()))
const selectedNotification = inject<Ref<SimpleNotification | null>>(
  'selectedNotification',
  ref(null),
)
const toggleNotificationSelection = inject<(id: string) => void>(
  'toggleNotificationSelection',
  () => {},
)
const selectNotification = inject<(notification: SimpleNotification) => void>(
  'selectNotification',
  () => {},
)
</script>

<template>
  <li class="isolate relative py-2 antialiased group">
    <!-- Checkbox that appears on hover only when selectable -->
    <div
      v-if="selectable"
      class="absolute left-2 top-1/2 z-10 transition-opacity duration-200 -translate-y-1/2"
      :class="[
        selectedNotificationIds.has(notification.id)
          ? 'opacity-100'
          : 'opacity-0 group-hover:opacity-100',
      ]"
    >
      <Checkbox
        :model-value="selectedNotificationIds.has(notification.id)"
        @update:model-value="() => toggleNotificationSelection(notification.id)"
      />
    </div>
    <button
      type="button"
      :role="isLinked ? undefined : 'button'"
      :tabindex="isLinked ? -1 : 0"
      @click="() => !isLinked && selectable && selectNotification(notification)"
      :class="[
        'block relative px-1 py-1.5 w-full text-left rounded-md overflow-hidden',
        isLinked ? 'cursor-pointer' : 'focus:outline-none',
        'hover:bg-gray-100/90 hover:dark:bg-gray-900/70',
        selectedNotification?.id === notification.id ? 'bg-gray-100/90 dark:bg-gray-900' : '',
      ]"
    >
      <!-- Extend touch target to entire field -->
      <span aria-hidden="true" class="absolute inset-0" />

      <!-- Text content container with transition -->
      <div
        :class="[
          'transition-transform duration-200 pr-8',
          selectable ? 'group-hover:translate-x-[2rem]' : '',
          selectedNotificationIds.has(notification.id) ? 'translate-x-[2rem]' : '',
        ]"
      >
        <div class="flex gap-2 items-center">
          <span
            v-if="!notification.read"
            aria-hidden="true"
            class="inline-flex rounded-full bg-primary-500 size-2 shrink-0 dark:bg-primary-500"
          />
          <h3 class="flex-1 text-sm font-medium text-gray-800 truncate dark:text-gray-50">
            {{ notification.subject }}
          </h3>
        </div>
        <p
          class="mt-0.5 text-xs leading-tight text-gray-500 break-words dark:text-gray-200"
          v-html="notification.message"
        ></p>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-500">
          {{ formatDate(notification.date) }}
        </p>
      </div>
    </button>
  </li>
</template>
